import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, inr, imageUrl, isProductInStock } from "@/lib/api";
import { StoreCategoryStrip } from "@/components/StoreCategoryStrip";
import { ProductCard } from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Search, ShieldCheck, Truck, Leaf, Flame, X as XIcon } from "lucide-react";
import { NAV, HOME } from "@/constants/testIds";

const HERO_IMG = "/assests/Banner/diwali-transparent-A.gif";

const categoryPath = (category, roots) => {
  const parent = roots.find((root) => root.children?.some((child) => child.id === category.id));
  return parent ? `/${parent.slug}/${category.slug}` : `/category/${category.slug}`;
};

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [combos, setCombos] = useState([]);
  const [query, setQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showSuggest, setShowSuggest] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggest(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [c, p, cb] = await Promise.all([
          api.get("/categories/tree"),
          api.get("/products", { params: { limit: 200 } }),
          api.get("/combos"),
        ]);
        setCategories(c.data);
        setProducts(p.data.filter(isProductInStock));
        setCombos(cb.data);
      } catch (e) {
        console.error("home load failed", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    let list = products;
    if (query) list = list.filter((p) => p.name.toLowerCase().includes(query.toLowerCase()));
    if (sortOrder === "price-asc") return [...list].sort((a, b) => Number(a.price) - Number(b.price));
    if (sortOrder === "price-desc") return [...list].sort((a, b) => Number(b.price) - Number(a.price));
    return list;
  }, [products, query, sortOrder]);

  const grouped = useMemo(() => {
    if (query || sortOrder !== "all") return null;
    // Group by the leaf (subcategory) when present, else by category
    const map = {};
    for (const p of products) {
      const key = p.subcategory_id || p.category_id;
      (map[key] ||= []).push(p);
    }
    const isDiwaliPackages = (category) =>
      [category.name, category.slug].some((value) =>
        value?.toLowerCase().replace(/[\s_]+/g, "-") === "diwali-packages",
      );

    // Show Diwali Packages and its subcategories before the rest of the catalog.
    const flat = [];
    const diwaliPackagesRoot = categories.find(isDiwaliPackages);
    if (diwaliPackagesRoot) {
      flat.push(diwaliPackagesRoot, ...(diwaliPackagesRoot.children || []));
    }
    for (const root of categories) {
      if (root.id === diwaliPackagesRoot?.id) continue;
      flat.push(root);
      for (const child of root.children || []) flat.push(child);
    }
    return flat.map((c) => ({ category: c, items: map[c.id] || [] })).filter((g) => g.items.length);
  }, [products, categories, query, sortOrder]);

  const subcategoryIds = useMemo(
    () => new Set(categories.flatMap((root) => (root.children || []).map((child) => child.id))),
    [categories],
  );
  const bannerAfterGroupIndex = grouped?.findIndex((_, index) =>
    grouped.slice(0, index + 1).filter(({ category }) => subcategoryIds.has(category.id)).length === 3,
  ) ?? -1;

  const sortOptions = [
    { key: "all", label: "All", icon: null },
    { key: "price-asc", label: "Price: Low to High", icon: null },
    { key: "price-desc", label: "Price: High to Low", icon: null },
  ];

  // Autocomplete suggestions (max 8): match name or brand
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const pool = [
      ...products.map((p) => ({ ...p, _kind: "product" })),
      ...combos.map((c) => ({ ...c, _kind: "combo" })),
    ];
    return pool
      .filter((p) => (p.name || "").toLowerCase().includes(q) || (p.brand || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, products, combos]);

  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);

  const goToSuggestion = (s) => {
    setShowSuggest(false);
    setQuery("");
    if (s._kind === "combo") navigate(`/combos/${s.slug || s.id}`);
    else navigate(`/products/${s.slug || s.id}`);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 pb-10">
      {/* Hero */}
      <section className="relative mt-4 rounded-3xl overflow-hidden border border-hyd-border shadow-card-dark">
        <div className="relative h-60 sm:h-72 grain-overlay">
          <img src={HERO_IMG} alt="Diwali fireworks" className="absolute inset-0 w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="relative h-full flex flex-col justify-end p-5 sm:p-8">
            <div className="text-xs uppercase tracking-[0.25em] text-[#FFD700] font-bold">Since 2001</div>
            <h1 className="font-display text-2xl sm:text-5xl font-extrabold text-white text-balance leading-tight mt-1">
              Hyderabad's Trusted <span className="text-[#FF6B60]">Exclusive Standard Crackers</span> Store
            </h1>
            <p className="text-white/80 text-sm sm:text-base mt-2 max-w-md">
              Delivering Diwali Celebrations since 2001, Hyderabad Crackers is your one stop solution for Standard Crackers in Hyderabad
            </p>
          </div>
        </div>
      </section>

      {/* Trust ribbon */}
      <section className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] sm:text-xs">
        {[
          { icon: ShieldCheck, label: "Genuine Standard Crackers" },
          { icon: Leaf, label: "Free Home Delivery" },
          { icon: Truck, label: "Delivery Only in Hyderabad" },
          { icon: Flame, label: "Min Order Value 2300" },
        ].map(({ icon: Icon, label }) => (
          <div key={label} className="flex items-center gap-2 bg-hyd-panel border border-hyd-border rounded-lg px-3 py-2 text-hyd-text">
            <Icon className="w-4 h-4 text-hyd-gold" />
            <span>{label}</span>
          </div>
        ))}
      </section>

      {/* Search + Pincode — STICKY + gold border */}
      <section className="mt-5 sticky top-[64px] z-30 -mx-4 px-4 py-3 bg-hyd-bg/90 backdrop-blur-md border-b border-hyd-border">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch">
          <div ref={searchRef} className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-hyd-gold z-10" />
            <Input
              data-testid={NAV.searchInput}
              placeholder="Search sparklers, chakras, rockets..."
              value={query}
              onChange={(e) => { setQuery(e.target.value); setShowSuggest(true); }}
              onFocus={() => setShowSuggest(true)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setShowSuggest(false);
                if (e.key === "Enter" && suggestions[0]) goToSuggestion(suggestions[0]);
              }}
              className="pl-9 pr-9 h-11 bg-hyd-panel border-2 border-hyd-gold text-hyd-text placeholder:text-hyd-text3 rounded-2xl shadow-glow-gold focus-visible:ring-hyd-gold"
            />
            {query && (
              <button
                type="button"
                onClick={() => { setQuery(""); setShowSuggest(false); }}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 grid place-items-center rounded-full text-hyd-text2 hover:text-hyd-red"
              >
                <XIcon className="w-4 h-4" />
              </button>
            )}
            {showSuggest && suggestions.length > 0 && (
              <div
                data-testid={HOME.searchSuggestions}
                className="absolute left-0 right-0 mt-2 bg-hyd-panel border border-hyd-gold/50 rounded-2xl overflow-hidden z-40 shadow-glow-gold max-h-96 overflow-y-auto"
              >
                {suggestions.map((s) => {
                  const img = imageUrl(s.images?.[s.primary_image_index || 0] || s.images?.[0] || s.image_url || "");
                  const catName = s._kind === "combo" ? "Combo pack" : (catMap[s.category_id]?.name || "Product");
                  return (
                    <button
                      key={`${s._kind}-${s.id}`}
                      type="button"
                      data-testid={HOME.searchSuggestion(s.id)}
                      onMouseDown={(e) => { e.preventDefault(); goToSuggestion(s); }}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-hyd-bg text-left transition-colors"
                    >
                      <div className="w-10 h-10 rounded-xl bg-hyd-panel2 overflow-hidden shrink-0">
                        {img && <img src={img} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-hyd-text truncate">{s.name}</div>
                        <div className="text-[11px] text-hyd-text3">{catName}</div>
                      </div>
                      <div className="text-hyd-gold font-bold text-sm shrink-0">{inr(s.price)}</div>
                    </button>
                  );
                })}
              </div>
            )}
            {showSuggest && query.trim() && suggestions.length === 0 && (
              <div className="absolute left-0 right-0 mt-2 bg-hyd-panel border border-hyd-border rounded-2xl px-4 py-3 text-sm text-hyd-text2 z-40">
                No products match &quot;{query}&quot;
              </div>
            )}
          </div>
        </div>
      </section>

      <h2 className="font-display text-base sm:text-lg font-bold mt-4 mb-2">Shop by category</h2>

      <StoreCategoryStrip inline showHeading={false} />

      <section>
        <ImageBanner
          image="/assests/Banner/SideBanner1.jpeg"
          alt="Light up your celebrations with Hyderabad Crackers"
          to="/category/al-a-carte"
        />
      </section>

      {/* Filter chips */}
      <section className="mt-4 sticky top-[64px] z-20 -mx-4 px-4 py-2 bg-hyd-bg/85 backdrop-blur">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {sortOptions.map((c) => {
            const Icon = c.icon;
            const active = sortOrder === c.key;
            return (
              <button
                key={c.key}
                onClick={() => setSortOrder(c.key)}
                className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                  active ? "bg-hyd-gold text-hyd-bg" : "bg-hyd-panel border border-hyd-border text-hyd-text2 hover:text-hyd-gold"
                }`}
              >
                {Icon && <Icon className="w-3.5 h-3.5" />} {c.label}
              </button>
            );
          })}
        </div>
      </section>

      {combos.length > 0 && (
        <section className="mt-6">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-display text-xl sm:text-2xl font-bold">Diwali Packages</h2>
            <a href="/combos" className="text-xs uppercase tracking-widest text-hyd-gold hover:underline">See all</a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {combos.slice(0, 4).map((cb) => (
              <ProductCard key={cb.id} product={cb} kind="combo" />
            ))}
          </div>
        </section>
      )}

      {/* Grid */}
      {loading ? (
        <div className="text-center py-12 text-hyd-text2">Loading crackers...</div>
      ) : grouped ? (
        <div className="space-y-8 mt-4">
          {grouped.map(({ category, items }, index) => (
            <React.Fragment key={category.id}>
              <section>
                <div className="flex items-baseline justify-between mb-3">
                  <h3 className="font-display text-xl sm:text-2xl font-bold text-hyd-text">{category.name}</h3>
                  <Link to={categoryPath(category, categories)} className="text-xs uppercase tracking-widest text-hyd-text2 hover:text-hyd-gold">
                    View all {items.length}
                  </Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {items.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
                </div>
              </section>
              {/* {index === bannerAfterGroupIndex && (
                <section>
                  <ImageBanner
                    image="https://images.unsplash.com/photo-1498931299472-f7a63a5a1cfa?w=1200&q=80&auto=format&fit=crop"
                    alt="Explore Hyderabad Crackers combo packs"
                    to="/combos"
                  />
                </section>
              )} */}
            </React.Fragment>
          ))}
        </div>
      ) : (
        <div className="mt-4">
          <div className="text-xs text-hyd-text2 mb-3">{filtered.length} items</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
          {filtered.length === 0 && (
            <div className="py-12 text-center text-hyd-text2">No products match your filters.</div>
          )}
        </div>
      )}

      {/* {(!grouped || bannerAfterGroupIndex < 0) && (
        <section className="mt-6">
          <ImageBanner
            image="https://images.unsplash.com/photo-1498931299472-f7a63a5a1cfa?w=1200&q=80&auto=format&fit=crop"
            alt="Explore Hyderabad Crackers combo packs"
            to="/combos"
          />
        </section>
      )} */}

    </div>
  );
}

function ImageBanner({ image, alt, to }) {
  return (
    <Link
      to={to}
      aria-label={alt}
      className="block overflow-hidden rounded-2xl border border-hyd-border shadow-card-dark group"
    >
      <img src={image} alt={alt} loading="lazy" className="block w-full h-auto transition-transform duration-500 group-hover:scale-[1.02]" />
    </Link>
  );
}
